import React, { useEffect, useMemo, useState } from "react";
import { authAPI } from "../services/api";
import "../styles/AdminTeachers.css";
import {
    FaUserTie,
    FaSearch,
    FaBan,
    FaCheckCircle,
    FaBook,
    FaClock,
    FaTimesCircle,
    FaChalkboardTeacher,
    FaUsers,
    FaRegFileAlt,
    FaEye,
    FaChartLine,
    FaIdBadge,
    FaEnvelope,
    FaCalendarAlt,
    FaTrash,
} from "react-icons/fa";

const AdminTeachers = () => {
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);

    // filters
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all"); // all | pending | active | disabled

    // dialog
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [activeTab, setActiveTab] = useState("overview"); // overview | stats | status

    useEffect(() => {
        fetchTeachers();
    }, []);

    const fetchTeachers = async () => {
        try {
            const response = await authAPI.getAllTeachers();
            setTeachers(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error("Error fetching teachers:", error);
            setTeachers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (teacher, newStatus) => {
        const actionName = newStatus === "active" ? "تفعيل" : "إيقاف/رفض";
        if (window.confirm(`هل أنت متأكد من ${actionName} حساب ${teacher.full_name}؟`)) {
            try {
                await authAPI.toggleUserStatus(teacher.id, newStatus);
                setTeachers((prev) => prev.map((t) => (t.id === teacher.id ? { ...t, status: newStatus } : t)));

                // لو الديالوج مفتوح على نفس المعلم، حدّثه أيضاً
                if (selectedTeacher?.id === teacher.id) {
                    setSelectedTeacher((prev) => ({ ...prev, status: newStatus }));
                }
            } catch (error) {
                console.error("Error updating status:", error);
                alert("فشل تحديث الحالة");
            }
        }
    };

    const handleDelete = async (teacher) => {
        const confirmMsg = `⚠️ تحذير: سيتم حذف حساب "${teacher.full_name}" نهائياً مع جميع الدورات والمحاضرات والامتحانات المرتبطة به.\n\nهل أنت متأكد من المتابعة؟`;

        if (window.confirm(confirmMsg)) {
            try {
                await authAPI.deleteTeacher(teacher.id);
                setTeachers((prev) => prev.filter((t) => t.id !== teacher.id));

                // إغلاق الديالوج إذا كان مفتوحاً
                if (selectedTeacher?.id === teacher.id) {
                    closeDetails();
                }

                alert("✅ تم حذف المعلم بنجاح");
            } catch (error) {
                console.error("Error deleting teacher:", error);
                alert("❌ فشل حذف المعلم");
            }
        }
    };

    // counts
    const counts = useMemo(() => {
        const all = teachers.length;
        const pending = teachers.filter((t) => t.status === "pending").length;
        const active = teachers.filter((t) => t.status === "active").length;
        const disabled = teachers.filter((t) => t.status === "disabled").length;

        const coursesTotal = teachers.reduce((sum, t) => sum + (Number(t.courses_count) || 0), 0);
        const lessonsTotal = teachers.reduce((sum, t) => sum + (Number(t.lessons_count) || 0), 0);
        const studentsTotal = teachers.reduce((sum, t) => sum + (Number(t.students_count) || 0), 0);
        const examsTotal = teachers.reduce((sum, t) => sum + (Number(t.exams_count) || 0), 0);

        return { all, pending, active, disabled, coursesTotal, lessonsTotal, studentsTotal, examsTotal };
    }, [teachers]);

    // filter list
    const filteredTeachers = useMemo(() => {
        const s = searchTerm.trim().toLowerCase();

        return teachers.filter((teacher) => {
            const fullName = (teacher.full_name || "").toLowerCase();
            const email = (teacher.email || "").toLowerCase();

            const matchesSearch = !s || fullName.includes(s) || email.includes(s);

            if (filterStatus === "all") return matchesSearch;
            return matchesSearch && teacher.status === filterStatus;
        });
    }, [teachers, searchTerm, filterStatus]);

    // helpers
    const formatDate = (d) => {
        if (!d) return "-";
        const date = new Date(d);
        if (Number.isNaN(date.getTime())) return "-";
        return date.toLocaleDateString("ar-EG");
    };

    const formatDateTime = (d) => {
        if (!d) return "-";
        const date = new Date(d);
        if (Number.isNaN(date.getTime())) return "-";
        return date.toLocaleString("ar-EG");
    };

    const getStatusLabel = (status) => {
        if (status === "active") return { text: "نشط", icon: <FaCheckCircle /> };
        if (status === "pending") return { text: "قيد المراجعة", icon: <FaClock /> };
        if (status === "disabled") return { text: "موقوف", icon: <FaBan /> };
        return { text: "غير معروف", icon: <FaTimesCircle /> };
    };

    const getActivityLevel = (teacher) => {
        // من الأفضل أن يأتي score من الباكيند لاحقاً (activity_score_7d)
        const score = Number(teacher.activity_score_7d);
        if (!Number.isNaN(score)) {
            if (score >= 70) return { label: "نشاط مرتفع", tone: "high" };
            if (score >= 40) return { label: "نشاط متوسط", tone: "mid" };
            return { label: "نشاط منخفض", tone: "low" };
        }

        // fallback بسيط: إذا عنده last_active_at خلال 7 أيام اعتبره متوسط
        if (teacher.last_active_at) {
            const diff = Date.now() - new Date(teacher.last_active_at).getTime();
            const days = diff / (1000 * 60 * 60 * 24);
            if (days <= 1) return { label: "نشاط مرتفع", tone: "high" };
            if (days <= 7) return { label: "نشاط متوسط", tone: "mid" };
            return { label: "نشاط منخفض", tone: "low" };
        }

        return { label: "غير متوفر", tone: "na" };
    };

    const openDetails = (teacher) => {
        setSelectedTeacher(teacher);
        setActiveTab("overview");
        setDetailsOpen(true);
    };

    const closeDetails = () => {
        setDetailsOpen(false);
        setSelectedTeacher(null);
    };

    const selectedStatus = selectedTeacher ? getStatusLabel(selectedTeacher.status) : null;
    const selectedActivity = selectedTeacher ? getActivityLevel(selectedTeacher) : null;

    return (
        <div className="admin-teachers-container fade-in" dir="rtl">
            {/* Header */}
            <div className="admin-header">
                <h1>
                    <FaUserTie /> إدارة المعلمين
                </h1>
                <p>لوحة تحكم لإدارة حسابات المعلمين ومراجعة نشاطهم.</p>
            </div>

            {/* Top KPIs (optional small row) */}
            <div className="kpi-row">
                <div className="kpi">
                    <div className="kpi-title">إجمالي المعلمين</div>
                    <div className="kpi-value">{counts.all}</div>
                </div>
                <div className="kpi">
                    <div className="kpi-title">إجمالي الدورات</div>
                    <div className="kpi-value">{counts.coursesTotal}</div>
                </div>
                <div className="kpi">
                    <div className="kpi-title">إجمالي المحاضرات</div>
                    <div className="kpi-value">{counts.lessonsTotal}</div>
                </div>
                <div className="kpi">
                    <div className="kpi-title">إجمالي الطلاب</div>
                    <div className="kpi-value">{counts.studentsTotal}</div>
                </div>
                <div className="kpi">
                    <div className="kpi-title">إجمالي الامتحانات</div>
                    <div className="kpi-value">{counts.examsTotal}</div>
                </div>
            </div>

            {/* Status Filter Cards */}
            <div className="teacher-filters">
                <button
                    className={`filter-card ${filterStatus === "all" ? "active" : ""} tone-all`}
                    onClick={() => setFilterStatus("all")}
                    type="button"
                >
                    <div className="label">الكل</div>
                    <div className="value">{counts.all}</div>
                </button>

                <button
                    className={`filter-card ${filterStatus === "pending" ? "active" : ""} tone-pending`}
                    onClick={() => setFilterStatus("pending")}
                    type="button"
                >
                    <div className="label">قيد المراجعة</div>
                    <div className="value">{counts.pending}</div>
                </button>

                <button
                    className={`filter-card ${filterStatus === "active" ? "active" : ""} tone-active`}
                    onClick={() => setFilterStatus("active")}
                    type="button"
                >
                    <div className="label">نشط</div>
                    <div className="value">{counts.active}</div>
                </button>

                <button
                    className={`filter-card ${filterStatus === "disabled" ? "active" : ""} tone-blocked`}
                    onClick={() => setFilterStatus("disabled")}
                    type="button"
                >
                    <div className="label">موقوف</div>
                    <div className="value">{counts.disabled}</div>
                </button>
            </div>

            {/* Search */}
            <div className="search-bar">
                <FaSearch className="search-icon" />
                <input
                    type="text"
                    placeholder="بحث عن معلم بالاسم أو البريد الإلكتروني..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Content */}
            {loading ? (
                <p className="loading-text">⏳ جارٍ تحميل بيانات المعلمين...</p>
            ) : (
                <>
                    {/* Cards Grid */}
                    <div className="teachers-grid">
                        {filteredTeachers.length > 0 ? (
                            filteredTeachers.map((teacher) => {
                                const st = getStatusLabel(teacher.status);
                                const activity = getActivityLevel(teacher);

                                const courses = Number(teacher.courses_count) || 0;
                                const lessons = Number(teacher.lessons_count) || 0;
                                const students = Number(teacher.students_count) || 0;
                                const exams = Number(teacher.exams_count) || 0;

                                return (
                                    <div key={teacher.id} className={`teacher-card card-${teacher.status || "unknown"}`}>
                                        <div className="card-head">
                                            <div className="teacher-main">
                                                <div className="avatar-circle">
                                                    {(teacher.full_name?.charAt(0) || "م").toUpperCase()}
                                                </div>
                                                <div className="teacher-meta">
                                                    <div className="teacher-name">{teacher.full_name || "مستخدم غير معرف"}</div>
                                                    <div className="teacher-email">
                                                        <FaEnvelope /> {teacher.email || "-"}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={`status-badge ${teacher.status || ""}`}>
                                                {st.icon} {st.text}
                                            </div>
                                        </div>

                                        <div className="card-sub">
                                            <div className="sub-item">
                                                <FaCalendarAlt />
                                                <span>تاريخ الانضمام:</span>
                                                <b>{formatDate(teacher.created_at)}</b>
                                            </div>

                                            <div className="sub-item">
                                                <FaChartLine />
                                                <span>النشاط:</span>
                                                <b className={`activity-pill ${activity.tone}`}>{activity.label}</b>
                                            </div>
                                        </div>

                                        <div className="stats-row">
                                            <div className="stat">
                                                <div className="stat-ico"><FaBook /></div>
                                                <div className="stat-label">الدورات</div>
                                                <div className="stat-value">{courses}</div>
                                            </div>

                                            <div className="stat">
                                                <div className="stat-ico"><FaChalkboardTeacher /></div>
                                                <div className="stat-label">المحاضرات</div>
                                                <div className="stat-value">{lessons}</div>
                                            </div>

                                            <div className="stat">
                                                <div className="stat-ico"><FaUsers /></div>
                                                <div className="stat-label">الطلاب</div>
                                                <div className="stat-value">{students}</div>
                                            </div>

                                            <div className="stat">
                                                <div className="stat-ico"><FaRegFileAlt /></div>
                                                <div className="stat-label">الامتحانات</div>
                                                <div className="stat-value">{exams}</div>
                                            </div>
                                        </div>

                                        <div className="card-actions">
                                            <button className="btn btn-secondary" type="button" onClick={() => openDetails(teacher)}>
                                                <FaEye /> عرض التفاصيل
                                            </button>

                                            {/* status actions */}
                                            {teacher.status === "pending" && (
                                                <>
                                                    <button
                                                        className="btn btn-success"
                                                        type="button"
                                                        onClick={() => handleUpdateStatus(teacher, "active")}
                                                    >
                                                        <FaCheckCircle /> قبول
                                                    </button>

                                                    <button
                                                        className="btn btn-danger"
                                                        type="button"
                                                        onClick={() => handleUpdateStatus(teacher, "disabled")}
                                                    >
                                                        <FaTimesCircle /> رفض
                                                    </button>
                                                </>
                                            )}

                                            {teacher.status === "active" && (
                                                <button
                                                    className="btn btn-danger"
                                                    type="button"
                                                    onClick={() => handleUpdateStatus(teacher, "disabled")}
                                                >
                                                    <FaBan /> إيقاف
                                                </button>
                                            )}

                                            {teacher.status === "disabled" && (
                                                <button
                                                    className="btn btn-success"
                                                    type="button"
                                                    onClick={() => handleUpdateStatus(teacher, "active")}
                                                >
                                                    <FaCheckCircle /> تفعيل
                                                </button>
                                            )}

                                            {/* Delete button - always available */}
                                            <button
                                                className="btn btn-danger-outline"
                                                type="button"
                                                onClick={() => handleDelete(teacher)}
                                                title="حذف المعلم نهائياً"
                                            >
                                                <FaTrash /> حذف
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="no-data-card">لا يوجد معلمين مطابقين للبحث/الفلترة.</div>
                        )}
                    </div>

                    {/* Export only (optional) */}
                    <div className="footer-actions">
                        <button
                            className="btn btn-outline"
                            type="button"
                            onClick={() => window.print()}
                            title="يمكنك لاحقاً استبدالها بتصدير CSV حقيقي من الباكيند"
                        >
                            تصدير (مبدئي)
                        </button>
                    </div>
                </>
            )}

            {/* Details Dialog */}
            {detailsOpen && selectedTeacher && (
                <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => {
                    if (e.target.classList.contains("modal-overlay")) closeDetails();
                }}>
                    <div className="modal">
                        <div className="modal-header">
                            <div className="modal-title">
                                <FaIdBadge />
                                تفاصيل المعلم
                            </div>
                            <button className="modal-close" type="button" onClick={closeDetails} aria-label="إغلاق">
                                ✕
                            </button>
                        </div>

                        <div className="modal-body">
                            {/* Teacher header */}
                            <div className="details-head">
                                <div className="details-user">
                                    <div className="avatar-circle big">
                                        {(selectedTeacher.full_name?.charAt(0) || "م").toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="details-name">{selectedTeacher.full_name || "-"}</div>
                                        <div className="details-email">
                                            <FaEnvelope /> {selectedTeacher.email || "-"}
                                        </div>
                                    </div>
                                </div>

                                <div className="details-badges">
                                    <span className={`status-badge ${selectedTeacher.status || ""}`}>
                                        {selectedStatus?.icon} {selectedStatus?.text}
                                    </span>
                                    <span className={`activity-pill ${selectedActivity?.tone || "na"}`}>
                                        <FaChartLine /> {selectedActivity?.label || "غير متوفر"}
                                    </span>
                                </div>
                            </div>

                            {/* Tabs */}
                            <div className="tabs">
                                <button
                                    className={`tab ${activeTab === "overview" ? "active" : ""}`}
                                    onClick={() => setActiveTab("overview")}
                                    type="button"
                                >
                                    نظرة عامة
                                </button>
                                <button
                                    className={`tab ${activeTab === "stats" ? "active" : ""}`}
                                    onClick={() => setActiveTab("stats")}
                                    type="button"
                                >
                                    الإحصائيات
                                </button>
                                <button
                                    className={`tab ${activeTab === "status" ? "active" : ""}`}
                                    onClick={() => setActiveTab("status")}
                                    type="button"
                                >
                                    الحالة والإجراءات
                                </button>
                            </div>

                            {/* Tab Panels */}
                            {activeTab === "overview" && (
                                <div className="tab-panel">
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <div className="info-label">المعرف</div>
                                            <div className="info-value">#{selectedTeacher.id}</div>
                                        </div>

                                        <div className="info-item">
                                            <div className="info-label">تاريخ الانضمام</div>
                                            <div className="info-value">{formatDate(selectedTeacher.created_at)}</div>
                                        </div>

                                        <div className="info-item">
                                            <div className="info-label">آخر نشاط</div>
                                            <div className="info-value">{formatDateTime(selectedTeacher.last_active_at)}</div>
                                        </div>

                                        <div className="info-item">
                                            <div className="info-label">آخر إجراء</div>
                                            <div className="info-value">{selectedTeacher.last_action || "-"}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "stats" && (
                                <div className="tab-panel">
                                    <div className="stats-row details">
                                        <div className="stat">
                                            <div className="stat-ico"><FaBook /></div>
                                            <div className="stat-label">الدورات</div>
                                            <div className="stat-value">{Number(selectedTeacher.courses_count) || 0}</div>
                                        </div>

                                        <div className="stat">
                                            <div className="stat-ico"><FaChalkboardTeacher /></div>
                                            <div className="stat-label">المحاضرات</div>
                                            <div className="stat-value">{Number(selectedTeacher.lessons_count) || 0}</div>
                                        </div>

                                        <div className="stat">
                                            <div className="stat-ico"><FaUsers /></div>
                                            <div className="stat-label">الطلاب</div>
                                            <div className="stat-value">{Number(selectedTeacher.students_count) || 0}</div>
                                        </div>

                                        <div className="stat">
                                            <div className="stat-ico"><FaRegFileAlt /></div>
                                            <div className="stat-label">الامتحانات</div>
                                            <div className="stat-value">{Number(selectedTeacher.exams_count) || 0}</div>
                                        </div>
                                    </div>


                                </div>
                            )}

                            {activeTab === "status" && (
                                <div className="tab-panel">
                                    <div className="status-actions">
                                        {selectedTeacher.status === "pending" && (
                                            <>
                                                <button
                                                    className="btn btn-success"
                                                    type="button"
                                                    onClick={() => handleUpdateStatus(selectedTeacher, "active")}
                                                >
                                                    <FaCheckCircle /> قبول وتفعيل
                                                </button>

                                                <button
                                                    className="btn btn-danger"
                                                    type="button"
                                                    onClick={() => handleUpdateStatus(selectedTeacher, "disabled")}
                                                >
                                                    <FaTimesCircle /> رفض/إيقاف
                                                </button>
                                            </>
                                        )}

                                        {selectedTeacher.status === "active" && (
                                            <button
                                                className="btn btn-danger"
                                                type="button"
                                                onClick={() => handleUpdateStatus(selectedTeacher, "disabled")}
                                            >
                                                <FaBan /> إيقاف الحساب
                                            </button>
                                        )}

                                        {selectedTeacher.status === "disabled" && (
                                            <button
                                                className="btn btn-success"
                                                type="button"
                                                onClick={() => handleUpdateStatus(selectedTeacher, "active")}
                                            >
                                                <FaCheckCircle /> إعادة تفعيل
                                            </button>
                                        )}

                                        {/* Delete button - always visible in status tab */}
                                        <button
                                            className="btn btn-danger-outline"
                                            type="button"
                                            onClick={() => handleDelete(selectedTeacher)}
                                            title="حذف المعلم نهائياً مع جميع بياناته"
                                        >
                                            <FaTrash /> حذف المعلم نهائياً
                                        </button>
                                    </div>

                                    <div className="hint-box">
                                        الإجراء هنا يؤثر مباشرة على حساب المعلم.
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" type="button" onClick={closeDetails}>
                                إغلاق
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTeachers;
